import {
  IAdminMfaSetup,
  IAdminMfaStatus,
  ICreateAdminSpec,
} from "../../types/api";
import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { toast } from "react-toastify";
import { deleteAdminQueryFn, editAdminQueryFn } from "./adminQueries";

export const createAdmin = async (adminToCreate: ICreateAdminSpec) => {
  const query = `mutation MyMutation($input: AdminInput!) {
            createAdmin(input: $input) {
            ... on Admin {
                member_id
                email
                first_name
                last_name
                }
            ... on Error {
                message
                code
            }
        }
    }
  `;

  try {
    const getCreateResponse = (await API.graphql({
      query: query,
      variables: { input: adminToCreate },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getCreateResponse.data.createAdmin;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const editAdmin = async (
  member_id: string,
  adminToEdit: ICreateAdminSpec,
  old_email: string
) => {
  const { query, inputs, variables } = editAdminQueryFn(member_id, adminToEdit, old_email);
  const myQuery = `mutation (${inputs}) {
        ${query}
        }`;
  try {
    const getEditResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getEditResponse.data.updateAdmin;
    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return response;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const deleteAdmin = async (member_id: string) => {
  const { query, variables, inputs } = deleteAdminQueryFn(member_id);
  const myQuery = `mutation (${inputs}) {
        ${query}
        }`;
  try {
    const getDeleteResponse = (await API.graphql({
      query: myQuery,
      variables: variables,
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const response = getDeleteResponse.data.deleteAdmin;

    if ("message" in response) {
      toast.error(response.message);
      return false;
    }
    return true;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const getAdminMfaStatus = async (
  member_id: string,
  access_token: string
) => {
  const query = `query MyQuery($member_id: String!, $access_token: String!) {
    getAdminMfaStatus(member_id: $member_id, access_token: $access_token) {
      ... on AdminMfaStatus {
        member_id
        email
        enabled
        preferred
        mfa_methods
      }
      ... on Error {
        message
        code
      }
    }
  }`;

  try {
    const response = (await API.graphql({
      query,
      variables: { member_id, access_token },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.getAdminMfaStatus;
    if ("message" in result) {
      toast.error(result.message);
      return null;
    }

    return result as IAdminMfaStatus;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const initiateAdminTotpSetup = async (
  member_id: string,
  access_token: string
) => {
  const query = `mutation MyMutation($member_id: String!, $access_token: String!) {
    initiateAdminTotpSetup(member_id: $member_id, access_token: $access_token) {
      ... on AdminMfaSetup {
        member_id
        email
        secret_code
        setup_uri
      }
      ... on Error {
        message
        code
      }
    }
  }`;

  try {
    const response = (await API.graphql({
      query,
      variables: { member_id, access_token },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.initiateAdminTotpSetup;
    if ("message" in result) {
      toast.error(result.message);
      return null;
    }

    return result as IAdminMfaSetup;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const verifyAdminTotpSetup = async (
  member_id: string,
  access_token: string,
  code: string
) => {
  const query = `mutation MyMutation($member_id: String!, $access_token: String!, $code: String!) {
    verifyAdminTotpSetup(member_id: $member_id, access_token: $access_token, code: $code) {
      ... on AdminMfaStatus {
        member_id
        email
        enabled
        preferred
        mfa_methods
      }
      ... on Error {
        message
        code
      }
    }
  }`;

  try {
    const response = (await API.graphql({
      query,
      variables: { member_id, access_token, code },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.verifyAdminTotpSetup;
    if ("message" in result) {
      toast.error(result.message);
      return null;
    }

    return result as IAdminMfaStatus;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};

export const disableAdminTotp = async (
  member_id: string,
  access_token: string,
  code: string
) => {
  const query = `mutation MyMutation($member_id: String!, $access_token: String!, $code: String!) {
    disableAdminTotp(member_id: $member_id, access_token: $access_token, code: $code) {
      ... on AdminMfaStatus {
        member_id
        email
        enabled
        preferred
        mfa_methods
      }
      ... on Error {
        message
        code
      }
    }
  }`;

  try {
    const response = (await API.graphql({
      query,
      variables: { member_id, access_token, code },
      authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
    })) as GraphQLResult<any>;

    const result = response.data.disableAdminTotp;
    if ("message" in result) {
      toast.error(result.message);
      return null;
    }

    return result as IAdminMfaStatus;
  } catch (error: any) {
    error.errors ? toast.error(error.errors[0].message) : toast.error(error);
    throw error;
  }
};
